/**
 * The five screens a new firm's administrator meets on their first sign-in.
 *
 * A shortcut through the first rows of Settings → Setup, not a second home for the same
 * facts: every screen writes through the same endpoints the settings pages use, and the
 * checklist reads the result back from what exists. What this file adds is the order, the
 * words, and the promise that anything can be skipped.
 *
 * Where the guide got to is written to the firm on every step, so leaving halfway and
 * signing in from another machine resumes at the screen that was left — and so that the
 * shell, which sends an administrator here only while the guide has never been seen,
 * stops doing so the moment it has.
 *
 * Full-screen rather than inside the shell, like the invitation page: the bar, the desk
 * and the telephone are what the person is being walked towards, and drawing them behind
 * the first screen would make the screen look like a dialog over something more important.
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { MapHome } from '@taxi/api-client';

import { useAuth } from '../auth/authContext';
import { mapApi, type MapConfiguration } from '../map/api';
import { PhoneLinesEditor } from '../settings/branding/PhoneLinesEditor';
import { withoutEmptyRows } from '../settings/branding/phoneLines';
import {
  aboutUsMaxLength,
  brandingApi,
  mapHomeApi,
  profileApi,
  settingsApi,
  setupApi,
  type BrandingSettings,
  type FirmProfile,
  type SetupGuideStage,
  type SetupState,
} from '../settings/api';
import { describe, refusal } from '../settings/errors';
import { staffApi, type StaffRole } from '../settings/fleet/staff';
import { notifySetupChanged } from '../settings/setup/events';
import { grouped, openItems, resumeStep } from '../settings/setup/items';
import type { HomeView } from '../settings/map/HomeMap';
import { openingView, sameView } from '../settings/map/homeView';
import { applyBrand } from '../theme';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Field,
  FieldErrors,
  Icon,
  Input,
  PageHeader,
  Pill,
  Select,
  Textarea,
  type FieldProblem,
} from '../ui';

const STEPS = 5;

/** The small map on the third screen. Lazy, because MapLibre is most of a megabyte. */
const HomeMap = lazy(async () => ({ default: (await import('../settings/map/HomeMap')).HomeMap }));

export function SetupGuide() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding, resolveTenant } = useAuth();

  const [state, setState] = useState<SetupState | null>(null);
  const [step, setStep] = useState(1);
  const [problem, setProblem] = useState<string | null>(null);

  /** A refusal about one of the lines, drawn under that line's number as well as above. */
  const [linesProblem, setLinesProblem] = useState<FieldProblem | null>(null);
  const [busy, setBusy] = useState(false);

  /** What the server holds, and the draft the screens edit. */
  const [loadedProfile, setLoadedProfile] = useState<FirmProfile | null>(null);
  const [profile, setProfile] = useState<FirmProfile | null>(null);
  const [loadedBranding, setLoadedBranding] = useState<BrandingSettings | null>(null);
  const [colour, setColour] = useState<string | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [uploading, setUploading] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * Screen three's map: where the firm's maps open, and where the person left it.
   *
   * Only sent when it was moved. A screen that saved the derived view as a choice would
   * freeze a guess the zones would otherwise keep improving, and Skip must leave it unset.
   */
  const [home, setHome] = useState<MapHome | null>(null);
  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);
  const [homeView, setHomeView] = useState<HomeView | null>(null);

  /** Screen four: whom the office has invited from here. */
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRole>('Dispatcher');
  const [sent, setSent] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const slug = branding?.slug ?? null;

  /**
   * Loaded once, on arrival.
   *
   * Saving the name or the colour retells the shell, which reloads the firm's language,
   * which hands every screen a new `t` — and an effect keyed on `t` would fetch the
   * recorded step again while this screen was already moving past it, and put it back.
   * The step is this screen's to advance from the moment it has been read.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [current, firm, look, where, style] = await Promise.all([
          setupApi.state(),
          profileApi.get(),
          brandingApi.get(),
          // The map is a courtesy on the third screen, and its two reads must never
          // stop the guide: a firm whose tile provider is down still gets its screens.
          mapHomeApi.get().catch(() => null),
          mapApi.configuration().catch(() => null),
        ]);

        if (cancelled) return;

        setState(current);
        setStep(resumeStep(current, STEPS));
        setLoadedProfile(firm);
        setProfile(firm);
        setLoadedBranding(look);
        setColour(look.primaryColor);
        setHome(where);
        setConfiguration(style);
        setHomeView(openingView(where, style));

        // A firm with a telephone system was given its numbers by the platform; the lines
        // customers ring are usually the same ones, so they are offered rather than typed
        // again. Asked and ignored on refusal: a firm without telephony has no numbers to
        // offer and the settings endpoint says so.
        if (firm.phoneLines.length === 0) {
          try {
            const telephony = await settingsApi.telephony();

            if (!cancelled && telephony.inboundNumbers.length > 0) {
              setProfile((draft) =>
                draft === null || draft.phoneLines.length > 0
                  ? draft
                  : {
                      ...draft,
                      phoneLines: telephony.inboundNumbers.map((number, index) => ({
                        label: '',
                        number,
                        kind: 'Booking',
                        shownToPassengers: true,
                        isSupportLine: index === 0,
                      })),
                    },
              );
            }
          } catch {
            // No telephone system, or none the firm may see. Nothing to prefill.
          }
        }
      } catch (failure) {
        if (!cancelled) setProblem(describe(failure, t, 'setup.couldNotLoad'));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival; `t` only words a failure
  }, []);

  /** Writes where the guide got to. A failure here is not worth stopping the person for. */
  const record = useCallback(async (stage: SetupGuideStage, at: number) => {
    try {
      await setupApi.guide({ stage, step: at });
      notifySetupChanged();
    } catch {
      // The screens still work; only the resume point is lost.
    }
  }, []);

  async function go(to: number) {
    await record('InProgress', to);
    setProblem(null);
    setStep(to);
  }

  async function dismiss() {
    setBusy(true);
    await record('Dismissed', 1);
    void navigate('/', { replace: true });
  }

  async function finish(destination: string) {
    setBusy(true);
    await record('Finished', STEPS);
    void navigate(destination, { replace: true });
  }

  /** Screen two: the name and the colour, saved together, and the header retold. */
  async function saveFirm() {
    if (profile === null || loadedProfile === null || loadedBranding === null || colour === null) {
      return go(3);
    }

    setBusy(true);
    setProblem(null);

    try {
      if (profile.displayName !== loadedProfile.displayName) {
        const stored = await profileApi.save(profile);
        setLoadedProfile(stored);
        setProfile(stored);
      }

      if (colour !== loadedBranding.primaryColor) {
        const stored = await brandingApi.save({ ...loadedBranding, primaryColor: colour });
        setLoadedBranding(stored);
      }

      if (slug !== null) await resolveTenant(slug);

      notifySetupChanged();
      await go(3);
    } catch (failure) {
      setProblem(describe(failure, t, 'branding.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  /** Screen three: the whole profile, whether or not anything on it changed — and the map, only if it moved. */
  async function saveReach() {
    if (profile === null) return go(4);

    setBusy(true);
    setProblem(null);

    try {
      // A row added and left empty is not a line, and goes before the rows are numbered.
      const tidy = { ...profile, phoneLines: withoutEmptyRows(profile.phoneLines) };
      setProfile(tidy);

      const stored = await profileApi.save(tidy);
      setLoadedProfile(stored);
      setProfile(stored);
      setLinesProblem(null);

      if (homeView !== null && !sameView(homeView, openingView(home, configuration))) {
        setHome(await mapHomeApi.save(homeView));
      }

      if (slug !== null) await resolveTenant(slug);

      notifySetupChanged();
      await go(4);
    } catch (failure) {
      setProblem(describe(failure, t, 'branding.couldNotSaveProfile'));
      setLinesProblem(refusal(failure, t, 'branding.couldNotSaveProfile'));
    } finally {
      setBusy(false);
    }
  }

  /** Skipping a screen puts back whatever it previewed. The colour is the one that shows. */
  async function skip(to: number) {
    if (loadedBranding !== null && colour !== loadedBranding.primaryColor) {
      setColour(loadedBranding.primaryColor);
      applyBrand(loadedBranding.primaryColor);
    }

    await go(to);
  }

  async function upload(file: File) {
    setUploading(true);
    setProblem(null);

    try {
      const result = await brandingApi.uploadLogo(file);

      setLoadedBranding((current) =>
        current === null ? current : { ...current, logoUrl: result.logoUrl },
      );
      setLogoVersion((version) => version + 1);

      if (slug !== null) await resolveTenant(slug);

      notifySetupChanged();
    } catch (failure) {
      setProblem(describe(failure, t, 'branding.couldNotUpload'));
    } finally {
      setUploading(false);
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  async function invite() {
    setBusy(true);
    setProblem(null);
    setNote(null);

    try {
      const invited = await staffApi.invite({
        email: email.trim(),
        roles: [role],
        displayName: name.trim() === '' ? null : name.trim(),
      });

      setSent((list) => [...list, invited.email]);
      setNote(
        invited.inviteExpiresAt === null
          ? t('setup.guide.people.existing', { email: invited.email })
          : t('setup.guide.people.invited', { email: invited.email }),
      );
      setEmail('');
      setName('');
      notifySetupChanged();
    } catch (failure) {
      setProblem(describe(failure, t, 'fleet.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  /** Screen five reads the list again, because three screens just changed it. */
  useEffect(() => {
    if (step !== STEPS) return;

    let cancelled = false;

    setupApi
      .state()
      .then((current) => {
        if (!cancelled) setState(current);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [step]);

  const firmName = profile?.displayName ?? branding?.displayName ?? '';

  return (
    <main className="min-h-screen bg-ground overflow-y-auto">
      <div className="max-w-[760px] mx-auto p-6 flex flex-col gap-4">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            {loadedBranding?.logoUrl != null ? (
              <img src={`${loadedBranding.logoUrl}?v=${logoVersion}`} alt="" className="h-[22px] w-auto" />
            ) : (
              <span className="w-[22px] h-[22px] rounded-control bg-brand" />
            )}
            <strong className="font-semibold truncate">{firmName}</strong>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-secondary text-ink-3">
              {t('setup.guide.stepOf', { step, total: STEPS })}
            </span>
            {/* Five marks, the ones passed in the brand colour: the step list NN/g asks
                for, small enough to sit beside the words. */}
            <span className="flex items-center gap-1" aria-hidden>
              {Array.from({ length: STEPS }, (_, index) => (
                <span
                  key={index}
                  className={
                    index < step
                      ? 'w-[18px] h-1.5 rounded-pill bg-brand'
                      : 'w-[18px] h-1.5 rounded-pill bg-surface-3'
                  }
                />
              ))}
            </span>
          </div>
        </header>

        {problem !== null && <Banner tone="danger">{problem}</Banner>}

        {state === null || profile === null || loadedBranding === null || colour === null ? (
          // Nothing loaded. While it is still coming, say so; once it has been refused —
          // somebody who is not an administrator typed the address — the sentence above
          // is the whole answer, and the only way forward is out.
          problem === null ? (
            <Card>
              <EmptyState>{t('app.loading')}</EmptyState>
            </Card>
          ) : (
            <div>
              <Button onClick={() => void navigate('/', { replace: true })}>
                <Icon name="back" />
                {t('nav.backToDispatch')}
              </Button>
            </div>
          )
        ) : (
          <Card>
            <div className="p-6 flex flex-col gap-5">
              {step === 1 && (
                <>
                  <PageHeader
                    title={t('setup.guide.welcome.title', { firm: firmName })}
                    description={t('setup.guide.welcome.body')}
                  />
                  <Actions>
                    <Button variant="quiet" disabled={busy} onClick={() => void dismiss()}>
                      {t('setup.guide.notNow')}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void go(2)}>
                      {t('setup.guide.start')}
                    </Button>
                  </Actions>
                </>
              )}

              {step === 2 && (
                <>
                  <PageHeader
                    title={t('setup.guide.firm.title')}
                    description={t('setup.guide.firm.body')}
                  />

                  <Field label={t('branding.displayName')} help={t('branding.displayNameHint')}>
                    <Input
                      value={profile.displayName}
                      maxLength={200}
                      disabled={busy}
                      onChange={(event) =>
                        setProfile({ ...profile, displayName: event.target.value })
                      }
                    />
                  </Field>

                  <Field label={t('branding.primaryColor')} help={t('branding.primaryColorHint')}>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        aria-label={t('branding.primaryColor')}
                        value={colour}
                        disabled={busy}
                        onChange={(event) => {
                          const next = event.target.value.toUpperCase();
                          setColour(next);

                          // Previewed on the whole screen, through the same correction the
                          // console applies at sign-in, so what they see is what their
                          // dispatchers will see. Skipping puts the stored colour back.
                          applyBrand(next);
                        }}
                        className="h-8 w-10 shrink-0 cursor-pointer rounded-control border border-rule bg-surface-2 p-1"
                      />
                      <Input
                        aria-label={t('branding.primaryColor')}
                        value={colour}
                        maxLength={7}
                        disabled={busy}
                        onChange={(event) => {
                          const next = event.target.value.toUpperCase();
                          setColour(next);
                          if (/^#[0-9A-F]{6}$/.test(next)) applyBrand(next);
                        }}
                      />
                    </div>
                  </Field>

                  <Field label={t('branding.logo')} help={t('branding.logoHint')}>
                    <div className="flex items-center gap-3">
                      {loadedBranding.logoUrl == null ? (
                        <span className="text-secondary text-ink-3">{t('branding.noLogo')}</span>
                      ) : (
                        <img
                          src={`${loadedBranding.logoUrl}?v=${logoVersion}`}
                          alt={t('branding.logo')}
                          className="h-12 w-auto"
                        />
                      )}
                      <input
                        ref={fileInput}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        aria-label={t('branding.chooseLogo')}
                        className="hidden"
                        disabled={uploading}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file !== undefined) void upload(file);
                        }}
                      />
                      <Button size="sm" disabled={uploading || busy} onClick={() => fileInput.current?.click()}>
                        {uploading ? t('branding.uploading') : t('branding.chooseLogo')}
                      </Button>
                    </div>
                  </Field>

                  {/* The header as it will be: the mark, the name, and the one solid
                      button a screen has, in the colour being chosen. */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-secondary font-medium text-ink-2">
                      {t('setup.guide.firm.preview')}
                    </span>
                    <div className="h-12 flex items-center justify-between gap-4 px-4 bg-surface border border-rule rounded-card">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {loadedBranding.logoUrl != null ? (
                          <img src={`${loadedBranding.logoUrl}?v=${logoVersion}`} alt="" className="h-[22px] w-auto" />
                        ) : (
                          <span className="w-[22px] h-[22px] rounded-control bg-brand" />
                        )}
                        <strong className="font-semibold truncate">{profile.displayName}</strong>
                        <span className="text-ink-3 truncate">{t('nav.area.dispatch')}</span>
                      </div>
                      <Button variant="primary" size="sm" tabIndex={-1}>
                        {t('nav.view.desk')}
                      </Button>
                    </div>
                  </div>

                  <Actions>
                    <Button variant="quiet" disabled={busy} onClick={() => void go(1)}>
                      <Icon name="back" />
                      {t('setup.guide.back')}
                    </Button>
                    <span className="flex-1" />
                    <Button variant="quiet" disabled={busy} onClick={() => void skip(3)}>
                      {t('setup.guide.skip')}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void saveFirm()}>
                      {busy ? t('setup.guide.saving') : t('setup.guide.next')}
                    </Button>
                  </Actions>
                </>
              )}

              {step === 3 && (
                <>
                  <PageHeader
                    title={t('setup.guide.reach.title')}
                    description={t('setup.guide.reach.body')}
                  />

                  <div className="flex flex-col gap-1.5">
                    <span className="text-secondary font-medium text-ink-2">
                      {t('branding.linesTitle')}
                    </span>
                    <div className="border border-rule rounded-card overflow-hidden">
                      <FieldErrors problem={linesProblem} onEdit={() => setLinesProblem(null)}>
                        <PhoneLinesEditor
                          lines={profile.phoneLines}
                          disabled={busy}
                          onChange={(phoneLines) => setProfile({ ...profile, phoneLines })}
                        />
                      </FieldErrors>
                    </div>
                  </div>

                  <Field label={t('branding.aboutUs')} help={t('branding.aboutUsHint')}>
                    <Textarea
                      rows={4}
                      maxLength={aboutUsMaxLength}
                      value={profile.aboutUs ?? ''}
                      disabled={busy}
                      onChange={(event) => setProfile({ ...profile, aboutUs: event.target.value })}
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('branding.website')} help={t('branding.urlHint')}>
                      <Input
                        type="url"
                        value={profile.website ?? ''}
                        disabled={busy}
                        onChange={(event) => setProfile({ ...profile, website: event.target.value })}
                      />
                    </Field>
                    <Field label={t('branding.email')} help={t('branding.emailHint')}>
                      <Input
                        type="email"
                        value={profile.email ?? ''}
                        disabled={busy}
                        onChange={(event) => setProfile({ ...profile, email: event.target.value })}
                      />
                    </Field>
                    <Field label={t('branding.openingHours')} help={t('branding.openingHoursHint')}>
                      <Input
                        value={profile.openingHours ?? ''}
                        maxLength={120}
                        disabled={busy}
                        onChange={(event) =>
                          setProfile({ ...profile, openingHours: event.target.value })
                        }
                      />
                    </Field>
                    <Field label={t('branding.officeAddress')} help={t('branding.officeAddressHint')}>
                      <Input
                        value={profile.officeAddress ?? ''}
                        maxLength={200}
                        disabled={busy}
                        onChange={(event) =>
                          setProfile({ ...profile, officeAddress: event.target.value })
                        }
                      />
                    </Field>
                  </div>

                  {/* Where the firm is, as the map every screen will open on. Smaller than
                      the Map page's, and without its search: the guide is a first pass, and
                      the page is where a firm three countries away goes to type a name. */}
                  {configuration !== null && homeView !== null && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-secondary font-medium text-ink-2">
                        {t('map.home.guide.title')}
                      </span>
                      <Suspense
                        fallback={
                          <div className="h-60 rounded-card border border-rule bg-surface-2" />
                        }
                      >
                        <HomeMap
                          configuration={configuration}
                          initial={homeView}
                          target={null}
                          onMoved={setHomeView}
                          className="h-60 rounded-card border border-rule overflow-hidden"
                        />
                      </Suspense>
                      <span className="text-secondary text-ink-3">{t('map.home.guide.body')}</span>
                    </div>
                  )}

                  <Actions>
                    <Button variant="quiet" disabled={busy} onClick={() => void go(2)}>
                      <Icon name="back" />
                      {t('setup.guide.back')}
                    </Button>
                    <span className="flex-1" />
                    <Button variant="quiet" disabled={busy} onClick={() => void skip(4)}>
                      {t('setup.guide.skip')}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void saveReach()}>
                      {busy ? t('setup.guide.saving') : t('setup.guide.next')}
                    </Button>
                  </Actions>
                </>
              )}

              {step === 4 && (
                <>
                  <PageHeader
                    title={t('setup.guide.people.title')}
                    description={t('setup.guide.people.body')}
                  />

                  {note !== null && <Banner tone="ok">{note}</Banner>}

                  <form
                    className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_170px_auto] gap-3 items-end"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void invite();
                    }}
                  >
                    <Field label={t('setup.guide.people.email')}>
                      <Input
                        type="email"
                        required
                        value={email}
                        disabled={busy}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    </Field>
                    <Field label={t('setup.guide.people.name')}>
                      <Input value={name} disabled={busy} onChange={(event) => setName(event.target.value)} />
                    </Field>
                    <Field label={t('fleet.columns.roles')}>
                      <Select
                        value={role}
                        disabled={busy}
                        onChange={(event) => setRole(event.target.value as StaffRole)}
                      >
                        <option value="Dispatcher">{t('setup.guide.people.dispatcher')}</option>
                        <option value="TenantAdmin">{t('setup.guide.people.admin')}</option>
                      </Select>
                    </Field>
                    <Button type="submit" disabled={busy || email.trim() === ''}>
                      {t('setup.guide.people.invite')}
                    </Button>
                  </form>

                  {sent.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-secondary font-medium text-ink-2">
                        {t('setup.guide.people.sent')}
                      </span>
                      <ul className="m-0 p-0 list-none flex flex-wrap gap-1.5">
                        {sent.map((address) => (
                          <li key={address}>
                            <Pill tone="ok" soft>
                              {address}
                            </Pill>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Actions>
                    <Button variant="quiet" disabled={busy} onClick={() => void go(3)}>
                      <Icon name="back" />
                      {t('setup.guide.back')}
                    </Button>
                    <span className="flex-1" />
                    <Button variant="quiet" disabled={busy} onClick={() => void go(5)}>
                      {t('setup.guide.skip')}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void go(5)}>
                      {t('setup.guide.next')}
                    </Button>
                  </Actions>
                </>
              )}

              {step === 5 && (
                <>
                  <PageHeader
                    title={t('setup.guide.done.title')}
                    description={t('setup.guide.done.body')}
                  />

                  <div className="flex items-center gap-4">
                    <strong className="text-stat font-semibold tracking-[-0.01em] text-brand">
                      {t('setup.percent', { percent: state.percent })}
                    </strong>
                    <div className="flex-1 h-2 rounded-pill bg-surface-3 overflow-hidden">
                      <div className="h-full bg-brand" style={{ width: `${state.percent}%` }} />
                    </div>
                    <span className="text-secondary text-ink-3">
                      {t('setup.progress', { settled: state.settled, total: state.total })}
                    </span>
                  </div>

                  {/* What is left, under the headings the Setup page uses, so the two read
                      as one list. The first group is the one that stops a booking, and its
                      rows carry the warn tone for the same reason they do on the page. */}
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    {grouped(openItems(state)).map((entry) => (
                      <div key={entry.group} className="flex flex-col gap-1.5">
                        <span className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
                          {t(`setup.group.${entry.group}`)}
                        </span>
                        <ul className="m-0 p-0 list-none flex flex-col gap-1">
                          {entry.items.map((item) => (
                            <li key={item.key} className="flex items-center gap-2 text-body">
                              <Icon
                                name={entry.group === 'beforeFirstBooking' ? 'alert' : 'chevron'}
                                small
                                className={
                                  entry.group === 'beforeFirstBooking'
                                    ? 'text-warn shrink-0'
                                    : 'text-ink-3 shrink-0 -rotate-90'
                                }
                              />
                              {t(`setup.item.${item.key}.title`, { defaultValue: item.key })}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <Actions>
                    <Button variant="quiet" disabled={busy} onClick={() => void go(4)}>
                      <Icon name="back" />
                      {t('setup.guide.back')}
                    </Button>
                    <span className="flex-1" />
                    <Button disabled={busy} onClick={() => void finish('/')}>
                      {t('setup.guide.toDesk')}
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void finish('/settings/setup')}>
                      {t('setup.guide.finish')}
                    </Button>
                  </Actions>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </main>
  );
}

/** The row of buttons under a screen. Back on the left, the way forward on the right. */
function Actions({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-2 pt-2">{children}</div>;
}
